import { useToast } from '~/hooks/use-toast';
import { useMemo, useRef, useState } from 'react';
import {
  CheckIcon,
  ChevronDown,
  ClipboardCheck,
  Download,
  Info,
  X,
} from 'lucide-react';
import { apiClient } from '~/lib/apiClient';
import { downloadCsvTemplate } from '~/lib/utils';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '~/components/ui/Accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/Table';
import { Separator } from '../ui/Separator';
import { Label } from '../ui/Label';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';

interface ParsedRow {
  [key: string]: string;
}

export function AmazonFileUploadForm({
  type,
  onUploadSuccess,
}: {
  type: string;
  onUploadSuccess?: () => void;
}) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [missingColumns, setMissingColumns] = useState<string[]>([]);
  const [allColumns, setAllColumns] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<ParsedRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  interface FileColumns {
    [key: string]: string[];
  }
  const AMAZON_FILE_COLUMNS: FileColumns = {
    'campaign-data': [
      'START_DATE',
      'END_DATE',
      'PORTFOLIO_NAME',
      'PROGRAM_TYPE',
      'CAMPAIGN_NAME',
      'COUNTRY',
      'STATUS',
      'CURRENCY',
      'BUDGET_AMOUNT',
      'TARGETING_TYPE',
      'BIDDING_STRATEGY',
      'IMPRESSIONS',
      'LAST_YEAR_IMPRESSIONS',
      'CLICKS',
      'LAST_YEAR_CLICKS',
      'CLICK-THRU_RATE_(CTR)',
      'SPEND',
      'LAST_YEAR_SPEND',
      'COST_PER_CLICK_(CPC)',
      'LAST_YEAR_COST_PER_CLICK_(CPC)',
      '14_DAY_TOTAL_ORDERS_(#)',
      'TOTAL_ADVERTISING_COST_OF_SALES_(ACOS)',
      'TOTAL_RETURN_ON_ADVERTISING_SPEND_(ROAS)',
      '14_DAY_TOTAL_SALES',
    ],
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setMissingColumns([]);
    if (file) {
      setFiles([file]);
      parseFile(file);
    }
  };
  function reset() {
    setFiles([]);
    setMissingColumns([]);
    setPreviewData([]);
    setShowPreview(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }
  async function parseFile(file: File) {
    try {
      const text = await readFile(file);
      const rows = text
        .trim()
        .split('\n')
        .map((row) => row.split(','));

      const headers = rows[0];
      setAllColumns(headers);

      const data: ParsedRow[] = rows.slice(1).map((row) =>
        headers.reduce((acc, header, index) => {
          acc[header] = row[index] || '';
          return acc;
        }, {} as ParsedRow),
      );

      setPreviewData(data);
    } catch (error) {
      reset();
    }
  }
  function readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }
  const columns = useMemo(() => {
    return AMAZON_FILE_COLUMNS[type] || [];
  }, [type]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!files.length) return;

    setIsLoading(true);
    setMissingColumns([]);

    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('intent', type);

      const response = await apiClient.postForm<{
        message?: string;
        detail?: { missing_columns?: string[] } | string;
      }>(`/connectors/file/amazon/${type}`, formData);

      if (response.message) {
        reset();
        toast({
          title: (
            <div className="flex items-center">
              <CheckIcon className="mr-2" />
              <span className="first-letter:capitalize">Success</span>
            </div>
          ),
          duration: 3000,
          variant: 'default',
          description: response.message,
        });
        onUploadSuccess?.();
      } else if (response.detail && typeof response.detail == 'string') {
        toast({
          title: 'Error',
          description: response.detail || 'Upload failed',
          variant: 'destructive',
        });
        reset();
      } else if (response.detail && typeof response.detail == 'object') {
        if (response.detail?.missing_columns) {
          setMissingColumns(response.detail?.missing_columns);
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Upload failed',
        variant: 'destructive',
      });
      reset();
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <Separator />
      <Accordion type="single" collapsible className="my-4">
        <AccordionItem value="item-1">
          <AccordionPrimitive.Header className="flex items-center">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between py-4 text-sm font-medium underline transition-all text-left [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <Info size={16} className="shrink-0 text-blue-500" />
                Make sure your file contains the following columns:
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            </AccordionPrimitive.Trigger>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                downloadCsvTemplate(columns, `amazon-${type}-template.csv`)
              }
              aria-label="Download Template CSV"
              className="ml-4"
            >
              <Download size={16} />
              Download Template
            </Button>
          </AccordionPrimitive.Header>
          <AccordionContent>
            <div className="border rounded-lg p-4  max-h-60 overflow-y-auto scrollbar">
              <ul className="list-none gap-2 grid md:grid-cols-2 lg:grid-cols-3">
                {columns.map((column, index) => (
                  <li
                    key={index}
                    className="grid grid-cols-6 p-2 rounded-lg text-sm border gap-1"
                  >
                    <span className="flex justify-center items-center">
                      <ClipboardCheck size={18} />
                    </span>
                    <span className="col-span-5 truncate" title={column}>
                      {column}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <form className="my-4" onSubmit={handleSubmit}>
        <Label htmlFor="file">Upload your csv file :</Label>
        <Input
          type="file"
          name="file"
          id="file"
          multiple={false}
          accept=".csv"
          required
          ref={fileInputRef}
          onChange={handleFileChange}
        />

        {files.length > 0 && (
          <div className="mt-4 max-w-full">
            <div className="flex items-center gap-2">
              <Button
                onClick={reset}
                variant={'destructive'}
                size={'sm'}
                type="button"
                aria-label="Remove File"
              >
                Remove File
              </Button>
              <Button
                onClick={() => setShowPreview(!showPreview)}
                variant={'outline'}
                size={'sm'}
                type="button"
                aria-label="Toggle Preview"
              >
                {showPreview ? 'Hide Data Preview' : 'Show Preview for 5 rows'}
              </Button>
            </div>

            {showPreview && (
              <div className="grid">
                <Table className="scrollbar overflow-x-auto">
                  <TableHeader>
                    <TableRow>
                      {allColumns.map((column) => (
                        <TableHead
                          key={column}
                          className="first-letter:capitalize"
                        >
                          {column}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.slice(0, 5).map((row, idx) => (
                      <TableRow key={idx}>
                        {allColumns.map((col) => (
                          <TableCell key={col}>{row[col] || '—'}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        <div className="inline-flex my-2 justify-end w-full">
          <Button
            type="submit"
            name="intent"
            value={type}
            disabled={!files.length || isLoading}
            aria-label="Upload"
          >
            {isLoading ? 'Uploading ...' : 'Upload'}
          </Button>
        </div>
      </form>

      {missingColumns.length ? (
        <div className="my-4">
          <Label className="text-red-500">
            Missing Columns in your file upload new file :
          </Label>
          <ul className="list-none list-inside text-red-500 text-sm grid md:grid-cols-3">
            {missingColumns.map((col, index) => (
              <li
                key={index}
                className="flex items-center gap-2 border m-1 border-red-400 rounded dark:border-red-700"
              >
                <X size="16" />
                {col}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}
